import html2canvas from 'html2canvas';
import { isObject } from 'lodash-es';
import cloneDeep from 'lodash/cloneDeep';

import { CUSTOM_METRIC, IMAGE_DOWNLOAD_FILE_FORMATS } from '@/app/dashboards/constants';
import { ALL_REPORT_FILTERS } from '@/app/dashboards/reportConstants';
import { getMediaTypeMetricParentName } from '@/app/dashboards/utils/media-types.utils';
import {
  ADS_REPORT_TYPES,
  BAR_CHART_REPORTS,
  COMPETITIVE_REPORTS,
  CREATOR_REPORTS,
  GRAPH_REPORTS,
  LINE_CHART_REPORTS,
  LINE_GRAPH_REPORT_TYPES,
  METRIC_TABLE_REPORTS,
  METRIC_TYPE_REPORT_KEYS,
  REPORT_CATEGORY_OPTIONS,
  REPORTS,
  SINGLE_METRIC_REPORT_TYPE,
} from '@/app/dashboards/utils/reports.enum';
import { env } from '@/env';
import {
  ADS_CHANNEL_TO_AGGREGATE_BY_TYPES,
  ADS_REPORT_AGGREGATE_BY_TYPES,
  AGGREGATE_REPORT_TYPES,
  COMMUNITY_REPORT_AGGREGATE_BY_TYPES,
  CREATOR_AGGREGATE_TYPES,
} from '@/models/dashboards/aggregate-type.enum';
import { CHANNELS } from '@/models/dashboards/channels.enum';
import { useMetricsStore } from '@/stores/metrics';

export function sortBrandsByName(brandArray) {
  return brandArray.sort((brandA, brandB) => brandA.name.localeCompare(brandB.name));
}

export function sortBrandsByHandle(brandArray) {
  return brandArray.sort((brandA, brandB) => brandA.handle.localeCompare(brandB.handle));
}

export function getBrandOccurrenceInfo(brandOccurrences, report, allBrands) {
  const brandIds = report?.meta?.brand_ids || [];
  brandIds.forEach((brandId) => {
    if (!brandOccurrences?.[brandId]) {
      const targetBrand = allBrands.find((brand) => brand.id === Number(brandId));
      let brandName = '';
      if (targetBrand) {
        brandName = targetBrand?.name || targetBrand?.label || '';
      }
      brandOccurrences[brandId] = {
        id: brandId,
        name: brandName,
        count: 1,
      };
    } else {
      brandOccurrences[brandId].count += 1;
    }
  });
}

export function getCompetitorOccurrenceInfo(competitorOccurrences, report, allCompetitors) {
  const competitiveSourceMap = {
    INSTAGRAM_COMPETITIVE: 'INSTAGRAM',
  };
  const competitorIds = report?.meta?.competitor_source_account_ids || [];
  const channel = report?.meta?.channel || report?.meta?.channels?.[0] || '';
  const channelCompetitors = allCompetitors?.[competitiveSourceMap[channel]] || [];
  let competitorHandle = '';

  competitorIds.forEach((competitorId) => {
    if (!competitorOccurrences?.[competitorId]) {
      const targetCompetitor = channelCompetitors.find(
        (competitor) => competitor.sourceAccountId === competitorId,
      );
      if (targetCompetitor) {
        competitorHandle = targetCompetitor?.handle || '';
      }
      competitorOccurrences[competitorId] = {
        id: competitorId,
        handle: competitorHandle,
        count: 1,
      };
    } else {
      competitorOccurrences[competitorId].count += 1;
    }
  });
}

export const extractReportType = (
  reportType,
  brands,
  metrics,
  channels,
  isCustomMetric = false,
) => {
  if (!brands.length || !metrics.length || !channels.length) {
    return reportType;
  }
  if (isCustomMetric) {
    return CUSTOM_METRIC;
  }
  if (!reportType) return reportType;
  if (reportType === 'GRAPH' && brands.length >= 2) {
    return LINE_GRAPH_REPORT_TYPES.GRAPH_MULTI_BRAND;
  }
  return reportType;
};

export function getMediaTypeOccurrenceInfo(
  mediaTypeOccurrence,
  mediaTypeMetricOccurrence,
  baseMetricsOccurrence,
  metrics,
  channels,
  reportType,
) {
  if (!metrics.length || !channels.length) {
    return;
  }

  const firstMetric = metrics[0];
  const channel = channels[0];
  const baseMetric = getMediaTypeMetricParentName(firstMetric, [channel], reportType);
  if (baseMetric) {
    baseMetricsOccurrence[baseMetric] = (baseMetricsOccurrence?.[baseMetric] || 0) + 1;
  }
}

export function getSimplifiedOccurrenceDict(occurrenceDict, fieldName) {
  return Object.values(occurrenceDict).reduce((results, item) => {
    const keyName = item[fieldName];
    results[keyName] = item.count;
    return results;
  }, {});
}

export function getTopicOccurrenceInfo(topicOccurrences, topicIds, topics) {
  topicIds.forEach((topicId) => {
    if (!topicOccurrences?.[topicId]) {
      const targetTopic = topics.find((topic) => topic.id === topicId);
      topicOccurrences[topicId] = {
        id: topicId,
        name: targetTopic?.name || '',
        count: 1,
      };
    } else {
      topicOccurrences[topicId].count += 1;
    }
  });
}

export function getComparisonOccurrenceInfo(
  comparisonOccurrences,
  comparisonTopicIds,
  comparisons,
) {
  comparisonTopicIds.forEach((comparisonTopicId) => {
    if (!comparisonOccurrences?.[comparisonTopicId]) {
      const targetComparison = comparisons.find(
        (comparison) => comparison.id === comparisonTopicId,
      );
      comparisonOccurrences[comparisonTopicId] = {
        id: comparisonTopicId,
        name: targetComparison?.name || '',
        count: 1,
      };
    } else {
      comparisonOccurrences[comparisonTopicId].count += 1;
    }
  });
}

export function getLocationValuesFromFilterOccurrences(locations) {
  return locations.map((location) => {
    const parts = [location?.city, location?.administrative_division, location?.country].filter(
      Boolean,
    );
    return parts.length > 0 ? parts.join(', ') : null;
  });
}

export function getFilterOccurrenceInfo(filterOccurrences, reportFilters) {
  reportFilters.forEach((filter) => {
    Object.keys(filter).forEach((key) => {
      let filterValue = filter[key];
      if (key === ALL_REPORT_FILTERS.LOCATION) {
        filterValue = getLocationValuesFromFilterOccurrences(filter[key]);
      }

      if (!filterOccurrences?.[key]) {
        filterOccurrences[key] = {
          id: key,
          value: filterValue,
          count: 1,
        };
      } else {
        filterOccurrences[key].count += 1;
        filterOccurrences[key].value = [
          ...new Set([...filterOccurrences[key].value, ...filterValue]),
        ];
      }
    });
  });
}

export function dashboardReportLoading(reportId, reportType, dashboardsStore, searchStore) {
  const isCompetitiveReport = COMPETITIVE_REPORTS.includes(reportType);

  if (isCompetitiveReport) {
    return (
      dashboardsStore.reportLoading[reportId] || searchStore.competitorsRequestStatus === 'pending'
    );
  }
  return dashboardsStore.reportLoading[reportId];
}

export function removeAccountInfoWhenAccountTagsExist(reportMeta) {
  if (reportMeta.brand_tags) {
    delete reportMeta.brand_ids;
  }
  if (reportMeta.competitor_tags) {
    delete reportMeta.competitor_source_account_ids;
  }
}

export function getGraphReportAggregateTypeOptions(
  aggregateTypeOptions,
  isLineChart = false,
  isDoughnutChart = false,
  isHorizontalChart = false,
) {
  return aggregateTypeOptions.map((value) => {
    const valueCopy = cloneDeep(value);
    let aggregateLabel = 'Columns';
    if (isHorizontalChart) {
      aggregateLabel = 'Rows';
    }
    if (isLineChart) {
      aggregateLabel = 'Lines';
    }
    if (isDoughnutChart) {
      aggregateLabel = 'Slices';
    }
    valueCopy.label = value.fullLabel || `${aggregateLabel} ${value.label}`;
    valueCopy.inputLabel = value.fullLabel || `${aggregateLabel} ${value.label}`;
    // Drop `text` to prevent dashing-ui from rendering this as a
    // display-only SpecialOptionDisplay header (see isSpecialOptionType).
    delete valueCopy.text;
    return valueCopy;
  });
}

export function getMetricReportAggregateTypeOptions(aggregateTypeOptions) {
  return aggregateTypeOptions.map((value) => {
    const valueCopy = cloneDeep(value);
    valueCopy.label = `Rows ${value.label}`;
    valueCopy.inputLabel = `Table ${value.label}`;
    delete valueCopy.text;
    return valueCopy;
  });
}

function getAdsReportAggregationOptions(report) {
  const reportType = report?.type ?? '';
  if ([REPORTS.ADS_GRAPH.value, REPORTS.ADS_TOTAL_METRIC.value].includes(reportType)) {
    return [];
  }

  // Ads reports only support 1 channel
  const channel = report.meta?.channels?.[0];
  // We need the targetMetricDetails from the backend to check if any aggregation options should be excluded
  const metricReportTypeKey =
    REPORTS[reportType]?.metricTypeReportKey ?? METRIC_TYPE_REPORT_KEYS.METRICS_REPORT;
  const metricsStore = useMetricsStore();
  const metricDetails = metricsStore.getTargetMetricDetails(
    channel,
    metricReportTypeKey,
    report.meta?.metrics?.[0],
  );

  const options = ADS_CHANNEL_TO_AGGREGATE_BY_TYPES[channel] ?? [];
  const excludedAggregations = cloneDeep(metricDetails?.excluded_aggregations ?? []);
  // TikTok ads doesn't have a sub breakdown when aggregating by campaign, therefore we exclude this option
  if (
    channel === CHANNELS.TIKTOK_ADS.value &&
    reportType === REPORTS.ADS_STACKED_BAR_METRIC.value
  ) {
    excludedAggregations.push(ADS_REPORT_AGGREGATE_BY_TYPES.SOURCE_CAMPAIGN.value);
  }
  if (!excludedAggregations) return options;

  // Filter out any aggregation options that are in the exclude list from the backend
  return options.filter((option) => !excludedAggregations.includes(option.value));
}

export function getReportAggregationOptionsByReportType(report) {
  const reportType = report?.type ?? '';

  if (ADS_REPORT_TYPES.includes(reportType)) {
    return getAdsReportAggregationOptions(report);
  }
  if (CREATOR_REPORTS.includes(reportType)) {
    return Object.values(CREATOR_AGGREGATE_TYPES);
  }
  if (report?.meta?.report_category === REPORT_CATEGORY_OPTIONS.COMMUNITY.id) {
    return Object.values(COMMUNITY_REPORT_AGGREGATE_BY_TYPES);
  }

  return Object.values(AGGREGATE_REPORT_TYPES);
}

export function getReportAggregateTypeOptions(report, { canAccessStatsEngineV2 = false } = {}) {
  const reportType = report?.type ?? '';
  const reportCategory = report?.meta?.report_category;
  let values = getReportAggregationOptionsByReportType(report);

  const combinedGraphAndBarReportTypes = BAR_CHART_REPORTS.concat(GRAPH_REPORTS);
  const isGraphReport = combinedGraphAndBarReportTypes.includes(reportType);
  const isMetricReport = METRIC_TABLE_REPORTS.includes(reportType);
  const isTSITotalMetricReport =
    reportCategory === REPORT_CATEGORY_OPTIONS.TSI.id &&
    Object.values(SINGLE_METRIC_REPORT_TYPE).includes(reportType);
  const isLineChart = LINE_CHART_REPORTS.includes(reportType);
  const isDoughnutChart = reportType === REPORTS.DOUGHNUT_METRIC.value;
  const isCompetitiveReport = reportType.startsWith('COMPETITIVE');

  if (!canAccessStatsEngineV2) {
    values = values.filter((value) => value.value !== AGGREGATE_REPORT_TYPES.MEDIA_TYPE.value);
  }

  if (isLineChart || isMetricReport || isTSITotalMetricReport) {
    values = values.filter((value) => value.value !== AGGREGATE_REPORT_TYPES.TAG.value);
  }

  if (isCompetitiveReport) {
    values = values.filter((value) => value.value !== AGGREGATE_REPORT_TYPES.CHANNEL.value);
    if (!canAccessStatsEngineV2) {
      values = values.filter((value) => value.value === AGGREGATE_REPORT_TYPES.BRAND.value);
    }
  }

  if (isGraphReport) {
    return getGraphReportAggregateTypeOptions(values, isLineChart, isDoughnutChart);
  }

  if (isMetricReport || isTSITotalMetricReport) {
    return getMetricReportAggregateTypeOptions(values);
  }

  return [];
}

export function downloadImage(element, format) {
  void html2canvas(element, {
    backgroundColor: '#ffffff',
    scale: 2,
    proxy: `${env.dashboardApiUrl}export_proxy`,
    onclone: (clonedDoc) => {
      // Sanitizing the CSS
      clonedDoc.querySelectorAll('.dashboard-report-item-content').forEach((item) => {
        item.style.backgroundColor = '#ffffff';
        item.style.boxShadow = 'none';
      });

      clonedDoc.querySelectorAll('.dashboard-report-item', '.vue-grid-item').forEach((item) => {
        item.style.backgroundColor = '#ffffff';
        item.style.boxShadow = 'none';
      });

      clonedDoc.querySelectorAll('[data-test="TableGraphReportHeader"]').forEach((item) => {
        item.style.display = 'flex';
      });

      // Hide only the three-dot menu buttons (not avatars or channel icons)
      // Target the entire menu button group container
      clonedDoc.querySelectorAll('[data-test="DashMenuButtonGroup"]').forEach((item) => {
        item.style.display = 'none';
      });

      // Also target individual menu buttons as fallback
      clonedDoc
        .querySelectorAll('button[aria-label="More Actions"], button[data-pc-name="button"]')
        .forEach((item) => {
          // Check if this button contains a three-dot icon
          const hasThreeDotsIcon =
            item.querySelector('svg[data-test="dots-vertical-icon"]') ||
            item.querySelector('svg path[d*="M10 5a2 2 0 1 1 4 0"]');

          if (hasThreeDotsIcon || item.getAttribute('aria-label') === 'More Actions') {
            item.style.display = 'none';
          }
        });

      clonedDoc.querySelectorAll('[data-test="ContentReportPanel"]').forEach((item) => {
        const cards = item.querySelectorAll('.card');
        cards.forEach((card) => {
          card.style.backgroundColor = '#ffffff';
        });
      });

      clonedDoc.querySelectorAll('[data-test="TableMetricReport"]').forEach((item) => {
        item.style.width = '520px';
      });

      clonedDoc.querySelectorAll('.media-card .media-badges svg').forEach((item) => {
        item.style.width = '20px';
        item.style.margin = '0';
      });
      clonedDoc.querySelectorAll('.card .stats-container dl dd .metric-stat').forEach((item) => {
        item.style.paddingLeft = '5px';
      });
      clonedDoc.querySelectorAll('.media-card .media img').forEach((item) => {
        item.style.position = 'absolute';
        item.style.top = '50%';
        item.style.left = '50%';
        item.style.height = 'auto';
        item.style.width = '100%';
        item.style.transform = 'translate(-50%, -50%)';
      });
      clonedDoc.querySelectorAll('.sort-order-text').forEach((item) => {
        item.style.paddingBottom = '2px';
      });
      clonedDoc
        .querySelectorAll(
          '.context-change-content.decrease svg, .context-change-content.decrease-in-green svg',
        )
        .forEach((item) => {
          item.style.transform = 'rotate(0deg)';
          item.style.width = '17px';
        });
      clonedDoc.querySelectorAll('.picker').forEach((item) => {
        item.style.background = 'none';
      });
      // Hide the download button. use class 'image-download'
      clonedDoc.querySelectorAll('.image-download, .see-more-button').forEach((item) => {
        item.style.display = 'none';
      });
      clonedDoc.querySelectorAll('.multi-brand-metric-report-panel').forEach((item) => {
        item.closest('.vue-grid-item').style.height = '';
      });
    },
  }).then((canvas) => {
    const formatKey = format.toUpperCase();
    const imageFormatKey = IMAGE_DOWNLOAD_FILE_FORMATS[formatKey];
    const dataURL = canvas.toDataURL(imageFormatKey.dataUrl);
    const url = dataURL.replace(/^data:image\/[^;]/, 'data:application/octet-stream');
    const link = document.createElement('a');
    const title = element.querySelectorAll('.metric-title')[0]?.textContent.trim();

    if (title) {
      link.download = `${title}.${imageFormatKey.extension}`;
    } else {
      link.download = `DH Report.${imageFormatKey.extension}`;
    }
    link.href = url;
    link.click();
  });
}

export function channelsInDashboard(dashboardReports) {
  const channelsWithDuplicates = dashboardReports.reduce((acc, report) => {
    const reportChannels = report?.meta?.channels || [].concat(report?.meta?.channel || []);
    return acc.concat(reportChannels);
  }, []);
  return [...new Set(channelsWithDuplicates)];
}

export function cleanMixpanelProperties(properties) {
  return Object.fromEntries(
    Object.entries(properties).filter(
      ([key, value]) =>
        key &&
        value &&
        (Array.isArray(value) ? value.length > 0 : true) &&
        (isObject(value) ? Object.keys(value).length > 0 : true),
    ),
  );
}
